import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../buttons/Button";
import CustomRadioButton from "../buttons/CustomRadioButton";
import useTranslation from "../CustomHooks/useTranslation";
import { inputFieldContent } from "../inputfields/InputFieldContent";

interface NotificationPreferenceModalProps {
  isOpen: boolean;
  onSave: (notificationType: string) => void;
  onClose?: () => void;
  loading?: boolean;
  defaultSelected?: string;
  headerText?: string;
  showTabs?: boolean;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  closeOnOutsideClick?: boolean;
  buttonText?: string;
}

const NotificationPreferenceModal: React.FC<NotificationPreferenceModalProps> = ({
  isOpen,
  onSave,
  onClose,
  loading = false,
  defaultSelected = "Email Address",
  headerText = "Mode Of Notification",
  showTabs = false,
  activeTab: externalActiveTab,
  onTabChange,
  closeOnOutsideClick = false,
  buttonText = "Submit",
}) => {
  const [selectedType, setSelectedType] = useState<string>(defaultSelected);
  const [internalActiveTab, setInternalActiveTab] = useState<string>("Mode Of Notifications");
  const { translate } = useTranslation(inputFieldContent);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Use external activeTab if provided, otherwise use internal state
  const activeTab = externalActiveTab || internalActiveTab;
  
  // Handle outside click
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        closeOnOutsideClick &&
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose?.();
      }
    };

    if (isOpen && closeOnOutsideClick) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, closeOnOutsideClick, onClose]);
  
  // Update selectedType when defaultSelected changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedType(defaultSelected);
    }
  }, [isOpen, defaultSelected]);

  // Handle tab change
  const handleTabChange = (tab: string) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }
  };

  const notificationOptions = [
    { id: "Email Address", label: "Email Address", testID: "email-radio" },
    { id: "Phone Number", label: "Phone Number", testID: "phone-number-radio" },
    { id: "In App Push", label: "In App Push", testID: "push-radio" },
  ];

  const handleSave = () => {
    onSave(selectedType);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[9999]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            ref={modalRef}
            className="bg-white rounded-lg shadow-lg p-6 max-w-[480px] w-full relative"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
          >
            <h2 className="text-heading5 font-semibold pb-4 mb-2 w-full border-b border-b-outline text-primary_text">
              {translate(headerText) || "Mode Of Notification"}
            </h2>
            
            {/* Tabs Section - Only show if showTabs prop is true */}
            {showTabs && (
              <div className="flex items-center mb-6 ">
                <button
                  onClick={() => handleTabChange("Mode Of Notifications")}
                  className={`mr-4 py-2 font-semibold ${
                    activeTab === "Mode Of Notifications"
                      ? "text-p_primary border-b-2 border-p_primary"
                      : "text-secondary_text"
                  }`}
                >
                  Mode Of Notifications
                </button>
                <button
                  onClick={() => handleTabChange("Logout Timeout")}
                  className={`px-4 py-2 font-semibold ${
                    activeTab === "Logout Timeout"
                      ? "text-p_primary border-b-2 border-p_primary"
                      : "text-secondary_text border-b-2 border-secondary_text"
                  } cursor-not-allowed`}
                  disabled={true}
                >
                  Logout Timeout
                </button>
              </div>
            )}

            {/* Show radio buttons only if active tab is "Mode Of Notifications" */}
            {(!showTabs || activeTab === "Mode Of Notifications") && (
              <div className="mb-[9px]">
                <CustomRadioButton
                  options={notificationOptions}
                  selected={selectedType}
                  onChange={setSelectedType}
                  gapClassName="gap-6"
                  padding="pb-5"
                />
              </div>
            )}

            {/* Show button only if active tab is "Mode Of Notifications" */}
            {(!showTabs || activeTab === "Mode Of Notifications") && (
              <div className="flex gap-6">
                <Button
                  buttonName={translate(buttonText) || "Submit"}
                  className="py-2.5 px-4 rounded-[6px]"
                  textSize="text-large"
                  buttonOnclick={handleSave}
                  testId="save-notification-preference-btn"
                  loading={loading}
                  buttonWidth="w-24"
                  buttonHight="h-[42px]"
                />
              </div>
            )}

            {/* Logout Timeout tab content - placeholder for now */}
            {showTabs && activeTab === "Logout Timeout" && (
              <div className="mb-6">
                <p className="text-body1 text-primary_text">
                  Logout Timeout settings will be implemented here
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationPreferenceModal;
